/**
 * FigmaInternationalBoard — 해외형 3-panel
 * Left: compact match cards | Right: match detail
 * Figma: 해외형 화이트.png
 */
import React, { useMemo } from 'react';
import { ReceiptText } from 'lucide-react';
import TeamLogo from '../TeamLogo';
import getCountryFlagUrl from './countryFlagResolver';
import getSportIconUrl from './sportIconResolver';
import getSelectionLabel from './selectionLabelResolver';
import { dedupeMergedSelections } from '../_helpers/marketDuplicateHelpers';
import { sortSelectionsByDisplayOrder } from '../_helpers/marketRowSorting';
import { SportsDetailSkeleton, SportsListSkeleton } from '../../../components/Skeleton/Skeleton';
import {
  enrichInplaySelectionMarketIdentity,
  getCustomerMarketKey,
  getSourceMarketId,
} from '../../../_helpers/inplayMarketIdentity';

const MKT_NAME_MAP = {
  'MATCH_RESULT': '승무패', '1X2': '승무패', 'MONEY_LINE': '승패',
  'HANDICAP': '핸디캡', 'ASIAN_HANDICAP': '핸디캡', 'Asian Handicap': '핸디캡',
  'SPREAD': '스프레드', 'OVER_UNDER': '오버언더', 'TOTALS': '토탈',
  'BTTS': '양팀득점', 'Double Chance': '더블찬스', 'DOUBLE_CHANCE': '더블찬스',
  'ODD_EVEN': '홀짝',
};

const normalizeSelectionName = (value) => String(value || '').trim().toLowerCase();

const MAIN_OUTCOME_ALIASES = {
  home: new Set(['1', 'home', 'h', 'home team', '홈', '홈승']),
  draw: new Set(['x', 'draw', 'd', '무', '무승부']),
  away: new Set(['2', 'away', 'a', 'away team', '원정', '원정승']),
};

const findMainOutcomeOdds = (oddsList, outcome) => {
  const odds = Array.isArray(oddsList) ? oddsList : [];
  const aliases = MAIN_OUTCOME_ALIASES[outcome];
  const found = odds.find((selection) => {
    const name = normalizeSelectionName(selection?.name);
    const nameKo = normalizeSelectionName(selection?.name_ko);
    return aliases.has(name) || aliases.has(nameKo);
  });
  if (found) return found;
  if (outcome === 'home') return odds[0] || null;
  if (outcome === 'away') return odds.length >= 2 ? odds[odds.length - 1] : null;
  return null;
};

const getMktTitle = (mkt) => {
  const mt = mkt.market_type || {};
  if (mt.name_ko) return mt.name_ko;
  if (mkt.name_ko) return mkt.name_ko;
  const name = mt.name || mkt.market_type_name || mkt.market_name || mkt.market_code || '';
  if (MKT_NAME_MAP[name]) return MKT_NAME_MAP[name];
  if (mkt.market_code && MKT_NAME_MAP[mkt.market_code]) return MKT_NAME_MAP[mkt.market_code];
  return name || '마켓';
};

/** HC 계열 (Asian / European / Spread) 감지 */
const _isHCMarket = (mkt) => {
  const code = String(mkt.market_code || mkt.market_type?.code || '');
  if (/^FEED_\d+_(3|13|53|61|64|65|66|67|95|250|281|342|407|433|550|935|1083|1248|1558|1693|1694|2286|2732)$/.test(code)) return true;
  const name = String(mkt.market_type?.name_ko || mkt.market_type?.name || mkt.market_type_name || mkt.name || '');
  return /핸디캡|handicap|spread/i.test(name);
};

const getMktTitleWithLine = (mkt) => {
  const title = getMktTitle(mkt);
  // 사용자 확정 규칙 (2026-04-08):
  //  - HC 계열: exact base_line 만 사용 (line 은 selection 방향값)
  //  - 비-HC: 기존 fallback (display_line || base_line || line)
  let lineLabel;
  if (mkt._isHCSet || _isHCMarket(mkt)) {
    lineLabel = (mkt._baseLineLabel ?? mkt.base_line ?? '').toString().trim();
  } else {
    lineLabel = String(mkt.display_line || mkt.base_line || mkt.line || '').trim();
  }
  if (!lineLabel) return title;
  const cleanLine = lineLabel.replace(/\s*\(0-0\)\s*$/, '');
  return `${title} (${cleanLine})`;
};

// Selection sort order by market type
const SEL_ORDER = { '1': 0, 'X': 1, 'x': 1, '2': 2, '1X': 0, 'X2': 1, '12': 2, 'Odd': 0, 'Even': 1, 'Over': 0, 'Under': 1 };
const sortSelections = (odds = []) => {
  const source = Array.isArray(odds) ? odds : [];
  const displayOrdered = sortSelectionsByDisplayOrder(source);
  if (source.some((odd) => odd?.displaySlot != null || odd?.displayOrder != null || odd?.display_slot != null || odd?.display_order != null)) {
    return displayOrdered;
  }
  return [...source].sort((a, b) => (SEL_ORDER[a.name] ?? 99) - (SEL_ORDER[b.name] ?? 99));
};

// Normalize markets: merge EVERY line of the same market type into one
// section, each line rendered as its own paired ROW within it.
//
// 사용자 확정 규칙 갱신 (2026-07-04):
//  - 한 market TYPE (핸디캡/오버언더 등) 의 모든 line 은 하나의 section 으로
//    묶여야 한다 — 이전 규칙("한 exact base_line = 한 market set")은 라인마다
//    헤더가 반복되는 문제(예: "Puck Line Reg Time (-2.5)"와 "(-2)"가 서로 다른
//    블록으로 분리)를 일으켰다. 이제 그 두 라인은 "Puck Line Reg Time" 이라는
//    하나의 헤더 아래 두 개의 ROW 로 표시된다.
//  - line 은 여전히 ROW 분할 기준이다 — 같은 section 안에서도 서로 다른
//    line 의 selection 이 한 row 에 섞이면 안 된다 (Home -2.5 / Away +2.5 가
//    한 row, Home -2 / Away +2 가 다른 row).
//
// HC split 의 source market_id 보존: 각 odds 가 어느 market_id 에서 왔는지 inject —
// SportsMain.handleOddsClick 이 정확한 market_id 를 cart 에 저장 가능하도록 (서버
// validation `배당과 마켓이 일치하지 않습니다` 에러 회피).
const _attachSourceMarketId = (mkt) => {
  const sourceMarketId = getSourceMarketId(null, mkt) ?? mkt.id ?? mkt.market_id;
  // initial REST suspend 도 inject — marketLatest 가 비어있는 첫 렌더에 fallback
  const sourceMarketSuspended = mkt.is_suspended ?? false;
  return (mkt.odds || mkt.selections || []).map(o => ({
    ...enrichInplaySelectionMarketIdentity(o, mkt),
    _sourceMarketSuspended: sourceMarketSuspended,
    _sourceLine: mkt.line ?? mkt.display_line ?? mkt.base_line ?? mkt.main_line ?? '',
    _sourceBaseLine: mkt.base_line ?? '',
  }));
};

/** Append the row's own line value to a selection label, unless the label
 * already spells it out (HC selection names already read e.g. "브리즈번
 * 라이트닝 -2.5 (-2.5)"; Over/Under names are just "오버"/"언더" and need the
 * line appended so lines merged into one section stay distinguishable —
 * matching a reference layout of "오버 (0.5)", "오버 (1.5)", ...). */
const withRowLineSuffix = (label, odd) => {
  const line = String(odd?._sourceLine ?? odd?._sourceBaseLine ?? '').trim();
  if (!line || String(label).includes(line)) return label;
  return `${label} (${line})`;
};

const normalizeMarkets = (markets) => {
  if (!markets) return [];
  const groups = new Map();
  for (const mkt of markets) {
    const mt = mkt.market_type || {};
    const typeKey = mt.name || mkt.market_type_name || mkt.market_code || 'unknown';
    const hcTypeKey = [
      mkt.market_type_id ?? mt.id ?? mkt.feed_market_id ?? mkt.market_code ?? typeKey,
      typeKey,
    ].filter(Boolean).join('::');
    const customerMarketKey = getCustomerMarketKey(mkt);
    const sourceMarketId = getSourceMarketId(null, mkt) ?? customerMarketKey;
    const isHC = _isHCMarket(mkt);
    // Group by market TYPE only — never by the exact line — so every line
    // of the same handicap/over-under market becomes a ROW inside one
    // shared section instead of its own repeated section header.
    const key = isHC ? `hc::${hcTypeKey}` : `nohc::${typeKey}`;
    const lineKey = (isHC ? (mkt.base_line ?? '') : (mkt.display_line || mkt.base_line || mkt.line || '')).toString().trim();
    const rowOdds = _attachSourceMarketId(mkt);
    if (!groups.has(key)) {
      groups.set(key, {
        ...mkt,
        _groupKey: key,
        _customerMarketKey: customerMarketKey,
        _sourceMarketId: sourceMarketId,
        _isHCSet: isHC,
        _baseLineLabel: lineKey || null,
        _mergedOdds: [],
        _lineRows: new Map(),
      });
    }
    const group = groups.get(key);
    group._mergedOdds.push(...rowOdds);
    // Row-level fallback key so a market with no line value at all (e.g.
    // Moneyline) still gets its own single row rather than merging with an
    // unrelated line-less market that happens to share a type key.
    const rowKey = lineKey || `row::${customerMarketKey || sourceMarketId || group._mergedOdds.length}`;
    if (!group._lineRows.has(rowKey)) group._lineRows.set(rowKey, []);
    group._lineRows.get(rowKey).push(...rowOdds);
  }
  return Array.from(groups.values()).map((group) => {
    // Row order follows the API's own market order (already correct in
    // practice — e.g. -2.5 before -2, 10.5 before 9.5) rather than being
    // re-sorted, since there's no single "right" direction across sports.
    const rows = Array.from(group._lineRows.values());
    const { _lineRows, ...rest } = group;
    return { ...rest, _rows: rows, _multiLine: rows.length > 1 };
  });
};

const FigmaInternationalBoard = ({
  effectiveBonusTiers, activeBonusFold,
  loading, matches,
  isSelected, handleOddsClick, oddsFlash, oddsLatest, marketLatest,
  formatOdds, formatDelta, formatTime, _fmt,
  getSportIcon, getMarkets, OddsBlock,
  goToPage, currentPage, totalPages, hasMoreMatches, hasPrevPage, matchTotal,
  matchCounts, liveCount, bettingType,
  selectedMatchId, handleMatchClick, matchDetail,
  sortOrder, setSortOrder,
  mobileCartOpen = false,
  onToggleMobileCart = () => {},
  cartCount = 0,
  isMobile = false,
}) => {

  // Card list: matches 배열 순서를 그대로 사용 (서버 정렬 보존)
  const cardList = useMemo(() =>
    matches.map(m => ({ match: m, league: m.league, sport: m.sport })),
  [matches]);

  const defaultMatch = cardList[0]?.match || null;
  const selected = selectedMatchId ? matches.find(m => m.id === selectedMatchId) : null;
  const listMatch = selected || defaultMatch;
  const detailBaseMatch = isMobile ? selected : listMatch;
  const isFreshDetail = matchDetail && detailBaseMatch && matchDetail.id === detailBaseMatch.id;
  const dm = isFreshDetail ? matchDetail : {};
  const activeMatch = detailBaseMatch ? {
    id: detailBaseMatch.id,
    home_team: dm.home_team || detailBaseMatch.home_team,
    away_team: dm.away_team || detailBaseMatch.away_team,
    home_team_ko: dm.home_team_ko || detailBaseMatch.home_team_ko,
    away_team_ko: dm.away_team_ko || detailBaseMatch.away_team_ko,
    home_team_logo_url: dm.home_team_logo_url || detailBaseMatch.home_team_logo_url,
    away_team_logo_url: dm.away_team_logo_url || detailBaseMatch.away_team_logo_url,
    live_score_home: dm.live_score_home ?? detailBaseMatch.live_score_home,
    live_score_away: dm.live_score_away ?? detailBaseMatch.live_score_away,
    start_time: dm.start_time || detailBaseMatch.start_time,
    sport_name: dm.sport_name || detailBaseMatch.sport_name,
    is_live: dm.is_live || detailBaseMatch.is_live,
    league: dm.league || detailBaseMatch.league,
    sport: detailBaseMatch.sport,
  } : null;
  const detailMarkets = isFreshDetail ? (dm.markets || dm.allMarkets) : null;
  const activeDetail = detailMarkets ? { allMarkets: detailMarkets } : (detailBaseMatch ? getMarkets(detailBaseMatch) : null);
  const isLoadingDetail = !!detailBaseMatch && !isFreshDetail;
  const showMobileDetail = Boolean(isMobile && selectedMatchId && activeMatch);

  const renderOddsBtn = (odds, match, market, label) => {
    if (!odds) return <div className="zi_odds_btn zi_odds_empty"><span className="zi_odds_val">-</span></div>;
    // suspend = odds self + source market (marketLatest 우선, 없으면 inject 된 _sourceMarketSuspended fallback)
    const oddsSelfSus = oddsLatest[odds.id]?.is_suspended ?? odds.is_suspended;
    const srcMktSus = marketLatest?.[getCustomerMarketKey(odds, market)]?.is_suspended ?? odds._sourceMarketSuspended ?? false;
    const sus = oddsSelfSus || srcMktSus;
    const flash = oddsFlash?.[odds.id];
    return (
      <button type="button"
        className={`zi_odds_btn ${isSelected(odds.id) ? 'zi_odds_sel' : ''} ${sus ? 'zi_odds_sus' : ''} ${flash ? 'zi_flash_' + flash.direction : ''}`}
        onClick={(e) => { e.stopPropagation(); !sus && handleOddsClick(match, market, odds); }}
        disabled={sus}>
        <span className="zi_odds_label">{label}</span>
        <span className="zi_odds_val">
          {formatOdds(oddsLatest[odds.id]?.odds ?? odds.odds, _fmt)}
        </span>
        {flash && <span className={`zi_delta zi_delta_${flash.direction}`}>{flash.direction === 'up' ? '+' : '-'}{formatDelta(flash.delta, _fmt)}</span>}
      </button>
    );
  };

  const resolveSelectionLabel = (odds, match, market, fallback) => (
    getSelectionLabel(odds, { market, match, fallback })
  );

  const renderSportIcon = (sport) => {
    const url = getSportIconUrl(sport);
    return url ? <img src={url} alt="" className="zi_sport_icon" /> : <span className="zi_sport_icon_text">{getSportIcon(sport)}</span>;
  };

  const buildLeagueLabel = (league) => {
    const leagueName = league?.name_ko || league?.name || '';
    const countryName = league?.countryInfo?.name_ko || league?.country || '';
    return countryName ? `[${countryName}] ${leagueName}` : leagueName;
  };

  const renderMobileListOddsBtn = (odds, match, market, label) => {
    const resolvedLabel = label || '-';
    if (!odds) {
      return (
        <div className="zi_m_list_odds zi_m_list_odds_empty">
          <span className="zi_m_list_odds_label">{resolvedLabel}</span>
          <span className="zi_m_list_odds_value">-</span>
        </div>
      );
    }

    const oddsSelfSus = oddsLatest[odds.id]?.is_suspended ?? odds.is_suspended;
    const srcMktSus = marketLatest?.[getCustomerMarketKey(odds, market)]?.is_suspended ?? odds._sourceMarketSuspended ?? false;
    const sus = oddsSelfSus || srcMktSus;
    const flash = oddsFlash?.[odds.id];

    if (sus) {
      return (
        <div className="zi_m_list_odds zi_m_list_odds_locked">
          <span className="zi_m_list_lock">🔒</span>
        </div>
      );
    }

    return (
      <button
        type="button"
        className={`zi_m_list_odds ${isSelected(odds.id) ? 'zi_odds_sel' : ''} ${flash ? `zi_flash_${flash.direction}` : ''}`}
        onClick={(event) => {
          event.stopPropagation();
          handleOddsClick(match, market, odds);
        }}
      >
        <span className="zi_m_list_odds_label">{resolvedLabel}</span>
        <span className="zi_m_list_odds_value">
          {formatOdds(oddsLatest[odds.id]?.odds ?? odds.odds, _fmt)}
        </span>
        {flash && <span className={`zi_delta zi_delta_${flash.direction}`}>{flash.direction === 'up' ? '+' : '-'}{formatDelta(flash.delta, _fmt)}</span>}
      </button>
    );
  };

  if (isMobile) {
    return (
      <div className="zi_m_board">
        <div className="zi_m_sort_row">
          <button
            type="button"
            className={`zi_m_sort_btn ${sortOrder === 'DESC' ? 'zi_sort_active' : 'zi_sort_muted'}`}
            onClick={() => setSortOrder('DESC')}
          >
            ↓ 내림차순 정렬
          </button>
          <button
            type="button"
            className={`zi_m_sort_btn ${sortOrder === 'ASC' ? 'zi_sort_active' : 'zi_sort_muted'}`}
            onClick={() => setSortOrder('ASC')}
          >
            ↑ 오름차순 정렬
          </button>
        </div>

        <div className="zi_m_list">
          {loading && <SportsListSkeleton count={4} mobile variant="international" />}
          {cardList.map(({ match: m, league, sport }) => {
            const { main, allMarkets } = getMarkets(m);
            const mainOdds = sortSelections(main?.odds || main?.selections || []);
            const home = m.home_team_ko || m.home_team;
            const away = m.away_team_ko || m.away_team;
            const flagUrl = getCountryFlagUrl(league);
            const homeOdds = findMainOutcomeOdds(mainOdds, 'home');
            const drawOdds = findMainOutcomeOdds(mainOdds, 'draw');
            const awayOdds = findMainOutcomeOdds(mainOdds, 'away');
            const plusN = Math.max(m.extra_market_count ?? Math.max((allMarkets?.length || 1) - 1, 0), 0);
            const isActive = selectedMatchId === m.id;

            return (
              <div key={m.id} className={`zi_m_card ${isActive ? 'zi_m_card_active' : ''}`}>
                <div className="zi_m_card_head">
                  <div className="zi_m_card_league_wrap">
                    {flagUrl && <img src={flagUrl} alt="" className="zi_m_card_flag" />}
                    {renderSportIcon(sport)}
                    <span className="zi_m_card_league">{buildLeagueLabel(league)}</span>
                  </div>
                  <div className="zi_m_card_badges">
                    {m.is_live && <span className="zi_m_live">LIVE</span>}
                    <span className="zi_m_card_time">{formatTime(m.start_time)}</span>
                  </div>
                </div>

                <div className="zi_m_card_body">
                  <div className="zi_m_card_teams">
                    <div className="zi_m_card_team">
                      <TeamLogo url={m.home_team_logo_url} name={home} className="zi_m_card_logo" />
                      <span>{home}</span>
                    </div>
                    <div className="zi_m_card_team">
                      <TeamLogo url={m.away_team_logo_url} name={away} className="zi_m_card_logo" />
                      <span>{away}</span>
                    </div>
                  </div>

                  <div className="zi_m_card_main">
                    {renderMobileListOddsBtn(homeOdds, m, main, resolveSelectionLabel(homeOdds, m, main, '홈승'))}
                    {drawOdds
                      ? renderMobileListOddsBtn(drawOdds, m, main, resolveSelectionLabel(drawOdds, m, main, '무'))
                      : <div className="zi_m_list_odds zi_m_list_static">VS</div>}
                    {renderMobileListOddsBtn(awayOdds, m, main, resolveSelectionLabel(awayOdds, m, main, '원정승'))}
                    <button
                      type="button"
                      className="zi_m_more_btn"
                      onClick={(event) => {
                        event.stopPropagation();
                        if (selectedMatchId !== m.id) handleMatchClick(m.id);
                      }}
                      aria-label="상세 베팅마켓 보기"
                    >
                      <span className="zi_m_more_text">+{plusN}</span>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}

          {!loading && matches.length === 0 && <div className="zxsb_empty">경기가 없습니다</div>}
        </div>

        {!loading && matchTotal > 0 && (
          <div className="zxsb_paging">
            <button type="button" className="zxsb_pg_btn" disabled={!hasPrevPage} onClick={() => goToPage(currentPage - 1)}>←</button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => i + 1).map((p) => (
              <button key={p} type="button" className={`zxsb_pg_btn ${currentPage === p ? 'zxsb_pg_active' : ''}`} onClick={() => goToPage(p)}>{p}</button>
            ))}
            <button type="button" className="zxsb_pg_btn" disabled={!hasMoreMatches} onClick={() => goToPage(currentPage + 1)}>→</button>
          </div>
        )}

        <div
          className={`zi_m_detail_scrim ${showMobileDetail ? 'zi_m_detail_scrim_open' : ''}`}
          onClick={() => selectedMatchId && handleMatchClick(selectedMatchId)}
        />

        <div className={`zi_m_detail_sheet ${showMobileDetail ? 'zi_m_detail_sheet_open' : ''}`}>
          <div className="zi_m_detail_topbar">
            <button
              type="button"
              className="zi_m_detail_back"
              onClick={() => selectedMatchId && handleMatchClick(selectedMatchId)}
            >
              ←
            </button>
            <div className="zi_m_detail_topbar_main">
              {activeMatch?.league && (
                <>
                  {(() => {
                    const detailFlag = getCountryFlagUrl(activeMatch.league);
                    return detailFlag ? <img src={detailFlag} alt="" className="zi_m_detail_flag" /> : null;
                  })()}
                  {renderSportIcon(activeMatch?.sport)}
                </>
              )}
              <span className="zi_m_detail_league">{activeMatch ? (activeMatch.league?.name_ko || activeMatch.league?.name || activeMatch.sport_name || '') : ''}</span>
            </div>
            <span className="zi_m_detail_time">{activeMatch ? formatTime(activeMatch.start_time) : ''}</span>
          </div>

          <div className="zi_m_detail_scroll">
            {isLoadingDetail ? <SportsDetailSkeleton mobile /> : activeMatch && (
              <>
                <div className="zi_m_detail_hero">
                  <div className="zi_m_detail_team">
                    <span className="zi_m_detail_team_name">{activeMatch.home_team_ko || activeMatch.home_team}</span>
                    <TeamLogo url={activeMatch.home_team_logo_url} name={activeMatch.home_team_ko || activeMatch.home_team} className="zi_m_detail_logo" />
                  </div>
                  <span className="zi_m_detail_vs">VS</span>
                  <div className="zi_m_detail_team">
                    <TeamLogo url={activeMatch.away_team_logo_url} name={activeMatch.away_team_ko || activeMatch.away_team} className="zi_m_detail_logo" />
                    <span className="zi_m_detail_team_name">{activeMatch.away_team_ko || activeMatch.away_team}</span>
                  </div>
                </div>

                <div className="zi_m_detail_markets">
                  {activeDetail && normalizeMarkets(activeDetail.allMarkets).map((mkt, mi) => {
                    const rows = (mkt._rows && mkt._rows.length > 0 ? mkt._rows : [mkt._mergedOdds || mkt.odds || mkt.selections || []])
                      .map((row) => sortSelections(dedupeMergedSelections(row, oddsLatest, marketLatest)));
                    return (
                      <div key={mi} className="zi_m_detail_section">
                        <div className="zi_m_detail_section_title">{mkt._multiLine ? getMktTitle(mkt) : getMktTitleWithLine(mkt)}</div>
                        {rows.map((row, ri) => (
                          <div key={ri} className="zi_m_detail_grid">
                            {row.map((o) => {
                              const oddsSelfSus = oddsLatest[o.id]?.is_suspended ?? o.is_suspended;
                              const srcMktSus = marketLatest?.[getCustomerMarketKey(o, mkt)]?.is_suspended ?? o._sourceMarketSuspended ?? false;
                              const sus = oddsSelfSus || srcMktSus;
                              const oFlash = oddsFlash?.[o.id];
                              return (
                                <button
                                  key={o.id}
                                  type="button"
                                  className={`zi_m_detail_btn ${isSelected(o.id) ? 'zi_odds_sel' : ''} ${sus ? 'zi_odds_sus' : ''} ${oFlash ? `zi_flash_${oFlash.direction}` : ''}`}
                                  onClick={() => !sus && handleOddsClick(activeMatch, mkt, o)}
                                  disabled={sus}
                                >
                                  <span className="zi_m_detail_btn_label">{withRowLineSuffix(getSelectionLabel(o, { market: mkt, match: activeMatch }), o)}</span>
                                  <span className="zi_m_detail_btn_value">
                                    {formatOdds(oddsLatest[o.id]?.odds ?? o.odds, _fmt)}
                                  </span>
                                  {oFlash && <span className={`zi_delta zi_delta_${oFlash.direction}`}>{oFlash.direction === 'up' ? '+' : '-'}{formatDelta(oFlash.delta, _fmt)}</span>}
                                </button>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          <button
            type="button"
            className={`zi_m_detail_cart_fab ${mobileCartOpen ? 'zi_m_detail_cart_fab_active' : ''}`}
            onClick={onToggleMobileCart}
          >
            <span className="zxsb_m_bottom_icon_wrap">
              <ReceiptText size={16} strokeWidth={2.1} />
              {cartCount > 0 && <span className="zxsb_m_bottom_badge">{cartCount}</span>}
            </span>
            <span>베팅카트</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="zi_board">
      {/* ── Left: match list ── */}
      <div className="zi_list">
        <div className="zi_list_header">
          <button type="button" className={`zi_sort_btn ${sortOrder === 'DESC' ? 'zi_sort_active' : 'zi_sort_muted'}`} onClick={() => setSortOrder('DESC')}>↓ 내림차순 정렬</button>
          <button type="button" className={`zi_sort_btn ${sortOrder === 'ASC' ? 'zi_sort_active' : 'zi_sort_muted'}`} onClick={() => setSortOrder('ASC')}>↑ 오름차순 정렬</button>
          <div style={{ flex: 1 }} />
        </div>

        <div className="zi_list_scroll">
          {loading && <SportsListSkeleton count={5} variant="international" />}
          {cardList.map(({ match: m, league, sport }) => {
                const { main } = getMarkets(m);
                const mainOdds = main?.odds || main?.selections || [];
                const home = m.home_team_ko || m.home_team;
                const away = m.away_team_ko || m.away_team;
                const isActive = listMatch?.id === m.id;
                const flagUrl = getCountryFlagUrl(league);

                return (
                  <div key={m.id}
                    className={`zi_card ${isActive ? 'zi_card_active' : ''}`}
                    onClick={() => handleMatchClick(m.id)}>
                    {/* Card header */}
                    <div className="zi_card_head">
                      {flagUrl && <img src={flagUrl} alt="" className="zi_card_flag" />}
                      {renderSportIcon(sport)}
                      <span className="zi_card_league">{league?.name_ko || league?.name}</span>
                      {m.is_live && <span className="zi_live">LIVE</span>}
                      <span className="zi_card_time">{formatTime(m.start_time)}</span>
                    </div>
                    {/* Card body: teams + odds */}
                    <div className="zi_card_body">
                      <div className="zi_card_teams">
                        <div className="zi_card_team">
                          <TeamLogo url={m.home_team_logo_url} name={home} className="zi_card_logo" />
                          <span>{home}</span>
                        </div>
                        <div className="zi_card_team">
                          <TeamLogo url={m.away_team_logo_url} name={away} className="zi_card_logo" />
                          <span>{away}</span>
                        </div>
                      </div>
                      <div className="zi_card_odds">
                        {(() => {
                          const { allMarkets } = getMarkets(m);
                          const plusN = m.extra_market_count ?? Math.max((allMarkets?.length || 1) - 1, 0);
                          return plusN > 0 ? <div className="zi_more_btn">+{plusN}</div> : null;
                        })()}
                        {renderOddsBtn(
                          findMainOutcomeOdds(mainOdds, 'home'),
                          m,
                          main,
                          resolveSelectionLabel(findMainOutcomeOdds(mainOdds, 'home'), m, main, '홈승')
                        )}
                        {mainOdds.length >= 3 && renderOddsBtn(
                          findMainOutcomeOdds(mainOdds, 'draw'),
                          m,
                          main,
                          resolveSelectionLabel(findMainOutcomeOdds(mainOdds, 'draw'), m, main, '무')
                        )}
                        {renderOddsBtn(
                          findMainOutcomeOdds(mainOdds, 'away'),
                          m,
                          main,
                          resolveSelectionLabel(findMainOutcomeOdds(mainOdds, 'away'), m, main, '원정승')
                        )}
                        {mainOdds.some(o => o.is_suspended) && <span className="zi_lock">🔒</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
          {!loading && matches.length === 0 && <div className="zxsb_empty">경기가 없습니다</div>}
        </div>

        {/* Pagination */}
        {!loading && matchTotal > 0 && (
          <div className="zxsb_paging">
            <button type="button" className="zxsb_pg_btn" disabled={!hasPrevPage} onClick={() => goToPage(currentPage - 1)}>← 이전</button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => i + 1).map(p => (
              <button key={p} type="button" className={`zxsb_pg_btn ${currentPage === p ? 'zxsb_pg_active' : ''}`} onClick={() => goToPage(p)}>{p}</button>
            ))}
            <button type="button" className="zxsb_pg_btn" disabled={!hasMoreMatches} onClick={() => goToPage(currentPage + 1)}>다음 →</button>
          </div>
        )}
      </div>

      {/* ── Right: detail panel ── */}
      <div className="zi_detail">
        {isLoadingDetail ? <SportsDetailSkeleton /> : activeMatch ? (
          <>
            {/* Detail header: 국기 + 종목 + 리그명 + 시간 */}
            <div className="zi_detail_head">
              {(() => { const f = getCountryFlagUrl(activeMatch.league); return f ? <img src={f} alt="" className="zi_detail_flag" /> : null; })()}
              {renderSportIcon(activeMatch.sport)}
              <span className="zi_detail_league">{activeMatch.league?.name_ko || activeMatch.league?.name || activeMatch.sport_name || ''}</span>
              <span className="zi_detail_time">{formatTime(activeMatch.start_time)}</span>
            </div>

            {/* Match hero */}
            <div className="zi_detail_hero">
              <div className="zi_detail_team">
                <TeamLogo url={activeMatch.home_team_logo_url} name={activeMatch.home_team_ko || activeMatch.home_team} className="zi_detail_logo" />
                <span>{activeMatch.home_team_ko || activeMatch.home_team}</span>
              </div>
              <div className="zi_detail_score">
                <span className="zi_detail_vs">{activeMatch.live_score_home ?? 0} vs {activeMatch.live_score_away ?? 0}</span>
              </div>
              <div className="zi_detail_team">
                <TeamLogo url={activeMatch.away_team_logo_url} name={activeMatch.away_team_ko || activeMatch.away_team} className="zi_detail_logo" />
                <span>{activeMatch.away_team_ko || activeMatch.away_team}</span>
              </div>
            </div>

            {/* Markets — normalized + sorted */}
            <div className="zi_detail_markets">
              {activeDetail && normalizeMarkets(activeDetail.allMarkets).map((mkt, mi) => {
                const rows = (mkt._rows && mkt._rows.length > 0 ? mkt._rows : [mkt._mergedOdds || mkt.odds || mkt.selections || []])
                  .map((row) => sortSelections(dedupeMergedSelections(row, oddsLatest, marketLatest)));
                return (
                  <div key={mi} className="zi_mkt_section">
                    <div className="zi_mkt_title">{mkt._multiLine ? getMktTitle(mkt) : getMktTitleWithLine(mkt)}</div>
                    {rows.map((row, ri) => (
                      <div key={ri} className="zi_mkt_grid zi_mkt_row">
                        {row.map(o => {
                          // suspend = odds self + source market (marketLatest 우선, fallback _sourceMarketSuspended)
                          const oddsSelfSus = oddsLatest[o.id]?.is_suspended ?? o.is_suspended;
                          const srcMktSus = marketLatest?.[getCustomerMarketKey(o, mkt)]?.is_suspended ?? o._sourceMarketSuspended ?? false;
                          const sus = oddsSelfSus || srcMktSus;
                          const oFlash = oddsFlash?.[o.id];
                          return (
                            <button key={o.id} type="button"
                              className={`zi_mkt_btn ${isSelected(o.id) ? 'zi_odds_sel' : ''} ${sus ? 'zi_odds_sus' : ''} ${oFlash ? 'zi_flash_' + oFlash.direction : ''}`}
                              onClick={() => !sus && handleOddsClick(activeMatch, mkt, o)}
                              disabled={sus}>
                              <span className="zi_mkt_label">{withRowLineSuffix(getSelectionLabel(o, { market: mkt, match: activeMatch }), o)}</span>
                              <span className="zi_mkt_val">
                                {formatOdds(oddsLatest[o.id]?.odds ?? o.odds, _fmt)}
                              </span>
                              {oFlash && <span className={`zi_delta zi_delta_${oFlash.direction}`}>{oFlash.direction === 'up' ? '+' : '-'}{formatDelta(oFlash.delta, _fmt)}</span>}
                            </button>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div className="zxsb_empty">경기를 선택하세요</div>
        )}
      </div>
    </div>
  );
};

export default FigmaInternationalBoard;
